import { ArtifactMetadata } from '@/types/artifact';

class HTMLParser {
  extractMetadata(htmlContent: string): ArtifactMetadata {
    const size = new Blob([htmlContent]).size;
    const characterCount = htmlContent.length;

    const dependencies = this.extractExternalDependencies(htmlContent);
    const hasExternalResources = dependencies.length > 0;

    return {
      size,
      characterCount,
      hasExternalResources,
      dependencies,
      estimatedRenderTime: this.estimateRenderTime(htmlContent),
    };
  }

  extractTitle(htmlContent: string): string {
    const titleMatch = htmlContent.match(/<title[^>]*>([^<]+)<\/title>/i);
    return titleMatch ? titleMatch[1].trim() : 'Untitled Artifact';
  }

  private extractExternalDependencies(htmlContent: string): string[] {
    const dependencies = new Set<string>();

    const urlRegexPatterns = [
      /src=["'](https?:\/\/[^"']+)["']/gi,
      /href=["'](https?:\/\/[^"']+)["']/gi,
      /url\(["']?(https?:\/\/[^"')]+)["']?\)/gi,
      /@import\s+["'](https?:\/\/[^"']+)["']/gi,
    ];

    urlRegexPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(htmlContent)) !== null) {
        dependencies.add(match[1]);
      }
    });

    return Array.from(dependencies);
  }

  private estimateRenderTime(htmlContent: string): number {
    const baseTime = 50;
    const contentFactor = Math.min(htmlContent.length / 10000, 5);
    const scriptTags = (htmlContent.match(/<script/gi) || []).length;
    const styleTags = (htmlContent.match(/<style/gi) || []).length;
    const linkTags = (htmlContent.match(/<link/gi) || []).length;

    const complexityFactor = (scriptTags * 20) + (styleTags * 10) + (linkTags * 5);

    return Math.round(baseTime + contentFactor + complexityFactor);
  }

  validateHTML(htmlContent: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!htmlContent.trim()) {
      errors.push('HTML content cannot be empty');
    }

    if (htmlContent.includes('<script') && htmlContent.includes('eval(')) {
      errors.push('Potentially unsafe script content detected');
    }

    if (htmlContent.length > 10 * 1024 * 1024) {
      errors.push('HTML content exceeds maximum size limit');
    }

    const unclosedTags = this.findUnclosedTags(htmlContent);
    if (unclosedTags.length > 0) {
      errors.push(`Unclosed tags detected: ${unclosedTags.join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private findUnclosedTags(htmlContent: string): string[] {
    const selfClosingTags = new Set([
      'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
      'link', 'meta', 'param', 'source', 'track', 'wbr'
    ]);

    const tagStack: string[] = [];
    const unclosedTags: string[] = [];

    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
    let match;

    while ((match = tagRegex.exec(htmlContent)) !== null) {
      const [fullMatch, tagName] = match;
      const isClosingTag = fullMatch.startsWith('</');
      const isSelfClosing = fullMatch.endsWith('/>') || selfClosingTags.has(tagName.toLowerCase());

      if (isClosingTag) {
        const lastOpenTag = tagStack.pop();
        if (lastOpenTag !== tagName.toLowerCase()) {
          unclosedTags.push(tagName);
        }
      } else if (!isSelfClosing) {
        tagStack.push(tagName.toLowerCase());
      }
    }

    unclosedTags.push(...tagStack);
    return [...new Set(unclosedTags)];
  }

  sanitizeHTML(htmlContent: string): string {
    return htmlContent
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, '');
  }
}

export const htmlParser = new HTMLParser(); 